// Google Ads conversie: geslaagde registratie van een bedrijf.
// Vuurt exact één keer per registratie — niet bij inloggen, niet bij een tweede
// render en niet als een pagina herladen wordt na de registratie.
//
// De gtag-basis staat in /cookie-consent.js (in de <head> van elke pagina) met
// Consent Mode v2: zonder cookie-toestemming stuurt Google een cookieloze ping
// en modelleert het de conversie, dus we hoeven hier niets af te schermen.

const CONVERSION_ID = 'AW-18355470334/jLCoCJfA8NccEP7_yLBE'

// Micro-conversie "Registratie gestart (bedrijf)": vuurt zodra iemand op
// /register de rol Bedrijf kiest. Lichter signaal (waarde €5 vs €50 voor de
// afgeronde registratie) zodat Smart Bidding iets te leren heeft zolang
// volledige registraties schaars zijn.
const START_CONVERSION_ID = 'AW-18355470334/ZxSOCKHO8eAcEP7_yLBE'

// Rollen die als bedrijfsregistratie tellen. Agency staat er bewust NIET in —
// zie het openstaande punt in de campagne-afspraken.
const CONVERTING_ROLES = ['bedrijf']

// Max wachttijd voordat we alsnog doorsturen als Google niet terugbelt.
const CALLBACK_TIMEOUT_MS = 1000

// Meta Pixel: bestaat alleen na cookie-toestemming (zie /cookie-consent.js).
// Geen fbq = geen toestemming of adblocker; dan gewoon niets sturen.
// eventId = hetzelfde id als de backend meestuurt via de Conversions API
// (utils/metaCapi.js), zodat Meta browser- en server-event ontdubbelt.
const fireMetaEvent = (eventName, eventId) => {
  try {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', eventName, {}, eventId ? { eventID: eventId } : undefined)
    }
  } catch {
    /* tracking mag nooit de flow breken */
  }
}

const sentKey = (userId) => `ugc4you:adsConversionSent:${userId || 'anon'}`

const alreadySent = (userId) => {
  try {
    return sessionStorage.getItem(sentKey(userId)) === '1'
  } catch {
    return false
  }
}

const markSent = (userId) => {
  try {
    sessionStorage.setItem(sentKey(userId), '1')
  } catch {
    /* private mode e.d. — dan liever dubbel meten dan niet meten */
  }
}

/**
 * Stuurt het conversie-event voor een nieuwe bedrijfsregistratie.
 * Resolvet zodra Google het event bevestigt, of na CALLBACK_TIMEOUT_MS.
 * Await dit vóór een redirect, anders kan het event verloren gaan.
 *
 * @param {object} user - de zojuist geregistreerde gebruiker
 * @returns {Promise<boolean>} of er daadwerkelijk een event is verstuurd
 */
export const trackSignupConversion = (user) => {
  const role = user?.role
  const userId = user?.id || user?._id

  if (!CONVERTING_ROLES.includes(role)) return Promise.resolve(false)
  if (alreadySent(userId)) return Promise.resolve(false)
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    console.warn('[ads] gtag niet beschikbaar — conversie niet verstuurd')
    return Promise.resolve(false)
  }

  markSent(userId)
  fireMetaEvent('CompleteRegistration', userId ? `reg_${userId}` : undefined)

  return new Promise((resolve) => {
    let done = false
    const finish = (sent) => {
      if (done) return
      done = true
      resolve(sent)
    }

    // Vangnet: als Google niet terugbelt (adblocker, trage verbinding) gaan we
    // na een seconde alsnog verder — de gebruiker mag hier niet op wachten.
    const timer = setTimeout(() => finish(true), CALLBACK_TIMEOUT_MS)

    window.gtag('event', 'conversion', {
      send_to: CONVERSION_ID,
      event_callback: () => {
        clearTimeout(timer)
        finish(true)
      }
    })
  })
}

/**
 * Stuurt de micro-conversie zodra een bezoeker de bedrijfsregistratie start
 * (rol gekozen, formulier nog niet ingevuld). Fire-and-forget: er volgt geen
 * redirect, dus geen callback/timeout nodig. Eén keer per sessie — de
 * gebruiker is hier nog anoniem, dus dedupe kan niet op user-id.
 *
 * @param {string} role - de gekozen rol
 * @returns {boolean} of er een event is verstuurd
 */
export const trackSignupStartConversion = (role) => {
  const key = 'ugc4you:adsStartSent'

  if (!CONVERTING_ROLES.includes(role)) return false
  try {
    if (sessionStorage.getItem(key) === '1') return false
  } catch {
    /* private mode e.d. — dan liever dubbel meten dan niet meten */
  }
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    console.warn('[ads] gtag niet beschikbaar — start-conversie niet verstuurd')
    return false
  }

  try {
    sessionStorage.setItem(key, '1')
  } catch {
    /* zie boven */
  }

  window.gtag('event', 'conversion', { send_to: START_CONVERSION_ID })
  return true
}

/**
 * Brief geplaatst — het echte conversiedoel van de Meta-campagnes.
 * Aangeroepen vanuit de createProject-wrapper in services/api.js, zodat élk
 * pad (wizard, create-pagina) meetelt. Concepten tellen niet mee. Elke
 * geplaatste brief is een eigen lead; de backend stuurt hetzelfde event
 * server-side met hetzelfde event-id. Alleen Meta; voor Google Ads bestaat (nog) geen
 * conversie-ID voor dit doel.
 */
export const trackBriefPlacedConversion = (projectId) => {
  fireMetaEvent('Lead', projectId ? `lead_${projectId}` : undefined)
}

export default trackSignupConversion
